import React, { createContext, useState } from "react";
import Axios from "../utils/Axios";
import Pagination from "../utils/Pagination";
import Search, { FilterItem, SortOrder } from "../utils/Search";

interface UsersInterface {
    getPendingUsers: () => void;
    getActiveUsers: (filters: FilterItem[], sortOrder: SortOrder, pageNumber?: number) => void;
    navigateUsers: (url: string, pageNumber: number) => void;
    approveUser: (rid: string) => void;
    rejectUser: (rid: string) => void;
    createUser: (data: any) => void;
    editUser: (data: any) => void;
    getUserById: (rid: string) => Promise<void | ActiveUser>;
    getPendingUserById: (rid: string) => Promise<void | PendingUser>;
    validateCustomerId: (customerId: string) => Promise<boolean | void>;
    checkCompanyCode: (countryCode: string, customerId: string) => Promise<boolean | void>;
    pendingUsers: PendingUser[];
    activeUsers: ActiveUser[];
    usersLoading: boolean;
    totalPendingUsers: number;
    totalActiveUsers: number;
    previousUrl: string;
    nextUrl: string;
    firstUrl: string;
    lastUrl: string;
    currentPage: number;
}

export interface PendingUser {
    rid: string;
    firstName: string;
    lastName: string;
    username: string;
    companyCode: string;
    countryCode: string;
    companyName: string;
    acceptable: boolean;
    matchedCompanyName: string;
    phoneNumber: string;
    createdAt: string;
}

export interface ActiveUser {
    uid: string;
    rid: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    country: string;
    username: string;
    language: string;
    role: string;
    activated: boolean;
    customerIds: any[];
    salesOrganization: string;
    geoFlag: string;
    comment: string;
}

export const UsersContext = createContext<UsersInterface>({
    getPendingUsers: () => {
        throw new Error('getPendingUsers() method not implemented');
    },
    getActiveUsers: () => {
        throw new Error('getActiveUsers() method not implemented');
    },
    navigateUsers: () => {
        throw new Error('navigateUsers() method not implemented');
    },
    approveUser: (rid: string) => {
        throw new Error('approveUser() method not implemented');
    },
    rejectUser: (rid: string) => {
        throw new Error('rejectUser() method not implemented');
    },
    createUser: (data: any) => {
        throw new Error('createUser() method not implemented');
    },
    editUser: (data: any) => {
        throw new Error('editUser() method not implemented');
    },
    getUserById: (rid: string) => {
        throw new Error('getUserById() method not implemented');
    },
    getPendingUserById: (rid: string) => {
        throw new Error('getPendingUserById() method not implemented');
    },
    validateCustomerId: async (customerId: string) => {
        throw new Error('validateCustomerId() method not implemented');
    },
    checkCompanyCode: async (countryCode: string, customerId: string) => {
        throw new Error('checkCompanyCode() method not implemented');
    },
    pendingUsers: [],
    activeUsers: [],
    usersLoading: false,
    totalPendingUsers: 0,
    totalActiveUsers: 0,
    previousUrl: '',
    nextUrl: '',
    firstUrl: '',
    lastUrl: '',
    currentPage: 0
});

function mapPendingUser(data: any) {
    return {
        rid: data['zuserprereg_rid'],
        firstName: data['zuserprereg_firstname'],
        lastName: data['zuserprereg_lastname'],
        username: data['zuserprereg_email'],
        companyCode: data['zuserprereg_company_code'],
        countryCode: data['zuserprereg_country_code'],
        companyName: data['zuserprereg_company_name'],
        acceptable: data['acceptable'],
        matchedCompanyName: data['company_name'],
        phoneNumber: data['zuserprereg_telno'],
        createdAt: data['zuserprereg_crmo']
    } as PendingUser;
}

function mapActiveUser(data: any) {
    const customerIds = data['companies'].map((element: string) => {
        return {label: element, value: element};
    });
    return {
        uid: data['zuser_uid'],
        rid: data['zuser_rid'],
        firstName: data['zuser_firstname'],
        lastName: data['zuser_lastname'],
        emailAddress: data['zuser_email'],
        phoneNumber: data['zuser_telno'],
        country: data['zuser_country'],
        username: data['zuser_login'],
        language: data['language'],
        role: data['zuser_typ'],
        activated: data['zuser_activated'],
        customerIds: customerIds,
        salesOrganization: data['zuser_salesorg_code'],
        geoFlag: data['zuser_geoflg'],
        comment: data['zuser_comment']
    } as ActiveUser;
}

function activeUserPayload(user: ActiveUser) {
    const customerIds = user.customerIds.map((element) => {
        return element.value;
    }); 
    return {
        'zuser_uid': user.uid,
        'zuser_rid': user.rid,
        'zuser_activated': user.activated,
        'zuser_firstname': user.firstName,
        'zuser_lastname': user.lastName,
        'zuser_email': user.emailAddress,
        'zuser_telno': user.phoneNumber,
        'zuser_country': user.country,
        'zuser_login': user.username,
        'language': user.language,
        'zuser_typ': parseInt(user.role),
        'companies': customerIds,
        'zuser_salesorg_code': user.salesOrganization,
        'zuser_geoflg': user.geoFlag,
        'zuser_comment': user.comment
    };
}

export const UsersProvider: React.FC = ({children}) => {

    const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
    const [activeUsers, setActiveUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [totalPendingUsers, setTotalPendingUsers] = useState(0);
    const [totalActiveUsers, setTotalActiveUsers] = useState(0);
    const [previousUrl, setPreviousUrl] = useState('');
    const [nextUrl, setNextUrl] = useState('');
    const [lastUrl, setLastUrl] = useState('');
    const [firstUrl, setFirstUrl] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const getPendingUsers = () => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        const url = '/user-pre-regs?page_size=50';
        Axios.get(url, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            const mapped = res.data.results.map((user: any) => mapPendingUser(user));
            setPendingUsers(mapped);
            setTotalPendingUsers(res.data.count);
            setPreviousUrl(res.data.previous);
            setNextUrl(res.data.next);
            setFirstUrl(url);
            setLastUrl(Pagination.getLastUrl(res.data.count, url));
            setUsersLoading(false);
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const getActiveUsers = (filters: FilterItem[], order: SortOrder, pageNumber?: number) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        const url = `/users${Search.getQueryString(filters, order)}`;
        if (pageNumber) setCurrentPage(pageNumber);
        Axios.get(url, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            const mapped = res.data.results.map((user: any) => mapActiveUser(user));
            setActiveUsers(mapped);
            setTotalActiveUsers(res.data.count);
            setPreviousUrl(res.data.previous);
            setNextUrl(res.data.next);
            setFirstUrl(url);
            setLastUrl(Pagination.getLastUrl(res.data.count, url));
            setUsersLoading(false);
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const getUserById = (rid: string) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        return Axios.get(`/users/${rid}`, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            const mapped = mapActiveUser(res.data);
            setUsersLoading(false);
            return mapped;
            
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const getPendingUserById = (rid: string) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        return Axios.get(`/user-pre-regs/${rid}`, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            const mapped = mapPendingUser(res.data);
            setUsersLoading(false);
            return mapped;
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const validateCustomerId = async (customerId: string) => {
        const accessToken = localStorage.getItem('access_token');
        const result = await Axios.get(`/companies?zcompany_code_exact=${customerId}`, {headers: {'Authorization': `Bearer ${accessToken}`} });
        return await result.data.count === 1;
    }

    const checkCompanyCode = async (countryCode: string, customerId: string) => {
        const result = await Axios.get(`/company-codes?zcompany_country_code=${countryCode}&zcompany_code=${customerId}`);
        return await result.data.count === 1;
    }

    const navigateUsers = (url: string, pageNumber: number) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        const navUrl = Pagination.getUrlAt(pageNumber, url);
        setCurrentPage(pageNumber);
        Axios.get(navUrl, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            const mapped = res.data.results.map((user: any) => mapActiveUser(user));
            setActiveUsers(mapped);
            setTotalActiveUsers(res.data.count);
            setPreviousUrl(res.data.previous);
            setNextUrl(res.data.next);
            setUsersLoading(false);
        }).catch(error => {
            setUsersLoading(false);
        });
    }

    const approveUser = (rid: string) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.get(`/user-pre-regs/${rid}/accept`, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            getPendingUsers();
            setUsersLoading(false);
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const createUser = (data: any) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.post('/users', data, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            getActiveUsers([], {label: '', order: ''});
            setUsersLoading(false);
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const editUser = (user: ActiveUser) => {
        const payload = activeUserPayload(user);
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.patch(`/users/${user.rid}`, payload, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            getActiveUsers([], {label: '', order: ''});
            setUsersLoading(false);
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    const rejectUser = (rid: string) => {
        setUsersLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.delete(`/user-pre-regs/${rid}`, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            getPendingUsers();
            setUsersLoading(false);
        }).catch((error) => {
            console.error(error);
            setUsersLoading(false);
        });
    }

    return <UsersContext.Provider value={{
        getActiveUsers,
        getPendingUsers,
        pendingUsers,
        activeUsers,
        usersLoading,
        approveUser,
        rejectUser,
        createUser,
        editUser,
        getUserById,
        getPendingUserById,
        validateCustomerId,
        checkCompanyCode,
        totalPendingUsers,
        totalActiveUsers,
        previousUrl,
        nextUrl,
        firstUrl,
        lastUrl,
        currentPage,
        navigateUsers
    }}>{children}</UsersContext.Provider>;
}



